import { useMemo, useRef, useState } from 'react';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import '@mantine/dropzone/styles.css';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  Progress,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import {
  IconArrowLeft,
  IconBolt,
  IconDownload,
  IconEdit,
  IconFolder,
  IconFolderShare,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { DataTable } from 'mantine-datatable';
import dayjs from 'dayjs';

import { mapLimit, sha256Hex, putToR2, hashRowsWithSha } from '../../lib/upload';
import { fromFileList, fromDataTransfer, type WalkedFile } from '../../lib/ingest-folder-walk';
import { contentTypeFor, shouldSkip } from '../../lib/ingest-mime';
import { buildFolderTree, type FolderNode } from '../../lib/folder-tree';
import {
  useIngestList,
  useIngestBatch,
  useFinalizeIngest,
  usePatchIngest,
  useDeleteIngest,
  usePromoteIngest,
  useIngestDownloadUrl,
} from '../../queries/ingest';
import { useOcrProviders, useOcrJobsForFile, useSubmitOcrJob } from '../../queries/ocr';
import { useProjectStore } from '../../project/project-store';
import { useAuthStore } from '../../auth/auth-store';
import { OcrProvidersTable } from './OcrProvidersTable';
import { humanSize } from './human-size';
import type { PipelineIngestFile } from '../../types/api';

type UploadPhase =
  | 'hashing'
  | 'batching'
  | 'uploading'
  | 'finalizing'
  | 'done'
  | 'duplicate'
  | 'rejected'
  | 'error';

interface RowState {
  clientId: string;
  walked: WalkedFile;
  contentType: string;
  size: number;
  sha?: string;
  serverId?: string;
  phase: UploadPhase;
  message?: string;
  loaded: number;
}

export function IngestPage() {
  const projectId = useProjectStore((s) => s.projectId);
  const projectRole = useProjectStore((s) => s.projectRole);
  const canWrite =
    projectRole === 'owner' || projectRole === 'moderator' || projectRole === 'editor';
  const me = useAuthStore((s) => s.user);
  const isAdminLike = me?.system_role === 'admin' || me?.system_role === 'superadmin';

  const [rows, setRows] = useState<RowState[]>([]);
  const [folderFilter, setFolderFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const batch = useIngestBatch();
  const finalize = useFinalizeIngest();
  const patch = usePatchIngest();
  const del = useDeleteIngest();
  const promote = usePromoteIngest();
  const downloadUrl = useIngestDownloadUrl();

  const filesQ = useIngestList({
    ...(folderFilter !== null ? { folder_prefix: folderFilter } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
  });

  const tree = useMemo(
    () => buildFolderTree((filesQ.data ?? []).map((f) => f.folder_path)),
    [filesQ.data],
  );

  function updateRow(clientId: string, patchObj: Partial<RowState>): void {
    setRows((prev) => prev.map((r) => (r.clientId === clientId ? { ...r, ...patchObj } : r)));
  }

  async function startUpload(walked: WalkedFile[]): Promise<void> {
    const filtered: WalkedFile[] = [];
    const skipped: WalkedFile[] = [];
    for (const w of walked) {
      if (shouldSkip(w.file.name)) {
        skipped.push(w);
        continue;
      }
      if (w.file.size === 0) {
        skipped.push(w);
        continue;
      }
      if (!contentTypeFor(w.file.name)) {
        skipped.push(w);
        continue;
      }
      filtered.push(w);
    }
    if (skipped.length > 0) {
      notifications.show({
        title: 'Skipped',
        message: `${skipped.length} unsupported / empty / hidden file(s) skipped`,
        color: 'yellow',
      });
    }
    if (filtered.length === 0) return;

    const seeded: RowState[] = filtered.map((w) => ({
      clientId: crypto.randomUUID(),
      walked: w,
      contentType: contentTypeFor(w.file.name)!,
      size: w.file.size,
      phase: 'hashing',
      loaded: 0,
    }));
    setRows((prev) => [...prev, ...seeded]);

    // Hash with concurrency 2 (RAM ceiling — 75 MB files can OOM weak machines).
    // The sha is threaded through the return value; reading it back off `seeded`
    // would always be undefined because those objects live in React state and
    // are never mutated (FE-B1).
    const withSha = await hashRowsWithSha(seeded, sha256Hex, {
      limit: 2,
      onHashed: (row, sha) => updateRow(row.clientId, { sha, phase: 'batching' }),
      onError: (row, err) =>
        updateRow(row.clientId, {
          phase: 'error',
          message: err instanceof Error ? err.message : 'hash_failed',
        }),
    });

    const ready = withSha.filter((r) => r.sha);
    const chunks: RowState[][] = [];
    for (let i = 0; i < ready.length; i += 250) chunks.push(ready.slice(i, i + 250));

    for (const chunk of chunks) {
      try {
        const resp = await batch.mutateAsync({
          files: chunk.map((r) => ({
            client_id: r.clientId,
            filename: r.walked.file.name,
            folder_path: r.walked.folderPath,
            content_type: r.contentType,
            size_bytes: r.size,
            sha256: r.sha!,
          })),
        });
        for (const d of resp.duplicates) {
          updateRow(d.client_id, {
            phase: 'duplicate',
            message: 'already in ingest pool',
          });
        }
        for (const j of resp.rejected) {
          updateRow(j.client_id, { phase: 'rejected', message: j.reason });
        }
        await mapLimit(resp.accepted, 5, async (a) => {
          updateRow(a.client_id, { serverId: a.id, phase: 'uploading' });
          const row = chunk.find((r) => r.clientId === a.client_id)!;
          try {
            await putToR2({
              url: a.upload.url,
              file: row.walked.file,
              headers: a.upload.headers,
              onProgress: (loaded) => updateRow(a.client_id, { loaded }),
            });
            updateRow(a.client_id, { phase: 'finalizing' });
            await finalize.mutateAsync(a.id);
            updateRow(a.client_id, { phase: 'done', loaded: row.size });
          } catch (err) {
            updateRow(a.client_id, {
              phase: 'error',
              message: err instanceof Error ? err.message : 'upload_failed',
            });
          }
        });
      } catch (err) {
        for (const r of chunk) {
          if (r.phase === 'batching') {
            updateRow(r.clientId, {
              phase: 'error',
              message: err instanceof Error ? err.message : 'batch_failed',
            });
          }
        }
      }
    }
  }

  function handleRename(file: PipelineIngestFile): void {
    let current = file.display_name ?? file.original_filename;
    modals.openConfirmModal({
      title: 'Rename file',
      children: (
        <TextInput
          label="Display name"
          defaultValue={current}
          autoFocus
          onChange={(e) => (current = e.currentTarget.value)}
        />
      ),
      labels: { confirm: 'Save', cancel: 'Cancel' },
      onConfirm: async () => {
        await patch.mutateAsync({ id: file.id, display_name: current });
        notifications.show({ title: 'Renamed', message: current, color: 'pine' });
      },
    });
  }

  function handleMove(file: PipelineIngestFile): void {
    let next = file.folder_path;
    modals.openConfirmModal({
      title: 'Move file',
      children: (
        <TextInput
          label="Folder path"
          description="Forward slashes for nesting. Empty = root."
          defaultValue={next}
          autoFocus
          onChange={(e) => (next = e.currentTarget.value)}
        />
      ),
      labels: { confirm: 'Move', cancel: 'Cancel' },
      onConfirm: async () => {
        await patch.mutateAsync({ id: file.id, folder_path: next });
        notifications.show({ title: 'Moved', message: next || '(root)', color: 'pine' });
      },
    });
  }

  function handleDelete(file: PipelineIngestFile): void {
    modals.openConfirmModal({
      title: 'Delete ingest file',
      children: (
        <Text>
          Remove <b>{file.display_name ?? file.original_filename}</b>? The R2 object stays but it
          will no longer appear in this pool.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await del.mutateAsync(file.id);
        notifications.show({ title: 'Deleted', message: file.original_filename, color: 'pine' });
      },
    });
  }

  function handlePromote(file: PipelineIngestFile): void {
    modals.openConfirmModal({
      title: 'Promote to document',
      children: (
        <Text>
          Promote <b>{file.display_name ?? file.original_filename}</b> into the project documents
          list? It will then be attachable to runsheet entries, contracts, tracts, etc.
        </Text>
      ),
      labels: { confirm: 'Promote', cancel: 'Cancel' },
      onConfirm: async () => {
        await promote.mutateAsync({ id: file.id });
        notifications.show({ title: 'Promoted', message: file.original_filename, color: 'pine' });
      },
    });
  }

  async function handleDownload(file: PipelineIngestFile): Promise<void> {
    try {
      const res = await downloadUrl.mutateAsync(file.id);
      const a = document.createElement('a');
      a.href = res.url;
      if (res.filename) a.download = res.filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      notifications.show({
        title: 'Download failed',
        message: err instanceof Error ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  const [ocrModalFile, setOcrModalFile] = useState<PipelineIngestFile | null>(null);

  if (!projectId) {
    return (
      <Stack>
        <Title order={2}>Pipeline ingest</Title>
        <Text c="dimmed">Pick a project to view its ingest pool.</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={2}>Pipeline ingest</Title>
          <Text c="dimmed" size="sm">
            Bulk-upload PDFs and images here. Files dedupe by hash within this project, preserve
            their folder structure, and can be promoted into the project's regular documents when
            you're ready to attach them.
          </Text>
        </Stack>
        <Group gap="xs">
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={14} />}
            component={Link}
            to="/documents"
          >
            Back to documents
          </Button>
        </Group>
      </Group>

      {canWrite && (
        <Dropzone
          onDrop={(dropped) =>
            startUpload(
              dropped.map((f) => ({
                file: f,
                relativePath: f.name,
                folderPath: '',
              })),
            )
          }
          onDropAny={async (event) => {
            // Mantine's onDrop strips folder paths, so we also listen for the
            // raw dataTransfer here when items contain directory entries.
            const dt = (event as unknown as DragEvent).dataTransfer;
            if (!dt) return;
            const hasDir = Array.from(dt.items).some(
              (it) =>
                (it as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntry | null })
                  .webkitGetAsEntry?.()?.isDirectory,
            );
            if (!hasDir) return;
            const walked = await fromDataTransfer(dt.items);
            await startUpload(walked);
          }}
          onReject={() =>
            notifications.show({
              title: 'Rejected',
              message: 'Unsupported file type',
              color: 'red',
            })
          }
          accept={[MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg, 'image/tiff', 'image/webp']}
          multiple
        >
          <Group justify="center" gap="xl" mih={120} style={{ pointerEvents: 'none' }}>
            <IconUpload size={40} />
            <Stack gap={4}>
              <Text size="md" fw={500}>
                Drop PDFs/images or whole folders here
              </Text>
              <Text size="xs" c="dimmed">
                Up to 500 files per batch. Folder structure is preserved.
              </Text>
            </Stack>
          </Group>
        </Dropzone>
      )}

      {canWrite && (
        <Group>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            // @ts-expect-error non-standard attribute
            webkitdirectory=""
            onChange={async (e) => {
              const fl = e.currentTarget.files;
              if (!fl) return;
              const walked = fromFileList(fl);
              await startUpload(walked);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
          <Button
            leftSection={<IconFolder size={14} />}
            variant="default"
            onClick={() => fileInputRef.current?.click()}
          >
            Pick a folder to ingest
          </Button>
        </Group>
      )}

      {rows.length > 0 && (
        <Stack gap={4}>
          <Title order={5}>Upload progress</Title>
          {rows.map((r) => (
            <Card key={r.clientId} withBorder padding="xs">
              <Group justify="space-between">
                <Stack gap={0}>
                  <Text size="sm" fw={500} lineClamp={1}>
                    {r.walked.relativePath}
                  </Text>
                  {r.message && (
                    <Text size="xs" c="dimmed">
                      {r.message}
                    </Text>
                  )}
                </Stack>
                <Badge
                  variant="light"
                  color={
                    r.phase === 'error' || r.phase === 'rejected'
                      ? 'red'
                      : r.phase === 'duplicate'
                      ? 'yellow'
                      : r.phase === 'done'
                      ? 'pine'
                      : 'blue'
                  }
                  tt="lowercase"
                >
                  {r.phase}
                </Badge>
              </Group>
              <Progress
                mt={4}
                value={
                  r.phase === 'uploading' && r.size > 0
                    ? (r.loaded / r.size) * 100
                    : r.phase === 'done'
                    ? 100
                    : 0
                }
                animated={r.phase === 'uploading'}
                color={r.phase === 'error' || r.phase === 'rejected' ? 'red' : 'pine'}
              />
            </Card>
          ))}
        </Stack>
      )}

      <Group align="flex-start" gap="md" wrap="nowrap">
        <Box w={220} style={{ flexShrink: 0 }}>
          <Stack gap="xs">
            <Title order={5}>Folders</Title>
            <FolderTreeView node={tree} selected={folderFilter} onSelect={setFolderFilter} />
          </Stack>
        </Box>

        <Stack gap="xs" style={{ flex: 1 }}>
          <Group>
            <TextInput
              placeholder="Search filename or display name"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={280}
            />
          </Group>
          <DataTable
            withTableBorder
            highlightOnHover
            minHeight={150}
            fetching={filesQ.isLoading}
            records={filesQ.data ?? []}
            idAccessor="id"
            columns={[
              {
                accessor: 'name',
                title: 'Name',
                render: (f) => (
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>
                      {f.display_name ?? f.original_filename}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {f.folder_path || '(root)'}
                    </Text>
                  </Stack>
                ),
              },
              {
                accessor: 'file_size_bytes',
                title: 'Size',
                width: 90,
                render: (f) => humanSize(Number(f.file_size_bytes)),
              },
              {
                accessor: 'status',
                title: 'Status',
                width: 110,
                render: (f) =>
                  f.promoted_at ? (
                    <Badge color="pine" variant="light">
                      promoted
                    </Badge>
                  ) : f.finalized_at ? (
                    <Badge color="blue" variant="light">
                      ready
                    </Badge>
                  ) : (
                    <Badge color="gray" variant="light">
                      draft
                    </Badge>
                  ),
              },
              {
                accessor: 'uploaded_at',
                title: 'Uploaded',
                width: 130,
                render: (f) => dayjs(f.uploaded_at).format('YYYY-MM-DD HH:mm'),
              },
              {
                accessor: 'actions',
                title: '',
                width: 200,
                textAlign: 'right',
                render: (f) => (
                  <Group gap={4} justify="flex-end">
                    <Tooltip label="Download">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => handleDownload(f)}
                        aria-label="Download"
                      >
                        <IconDownload size={16} />
                      </ActionIcon>
                    </Tooltip>
                    {canWrite && (
                      <>
                        <Tooltip label="Rename">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => handleRename(f)}
                            aria-label="Rename"
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Move">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => handleMove(f)}
                            aria-label="Move"
                          >
                            <IconFolderShare size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Promote to documents">
                          <ActionIcon
                            variant="subtle"
                            disabled={!!f.promoted_at || !f.finalized_at}
                            onClick={() => handlePromote(f)}
                            aria-label="Promote"
                          >
                            <IconUpload size={16} />
                          </ActionIcon>
                        </Tooltip>
                        {isAdminLike && f.finalized_at && (
                          <Tooltip label="Run OCR">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => setOcrModalFile(f)}
                              aria-label="Run OCR"
                            >
                              <IconBolt size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip label="Delete">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => handleDelete(f)}
                            aria-label="Delete"
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </>
                    )}
                  </Group>
                ),
              },
            ]}
          />
        </Stack>
      </Group>

      <OcrProvidersTable />

      <OcrJobsModal
        file={ocrModalFile}
        onClose={() => setOcrModalFile(null)}
      />
    </Stack>
  );
}

function FolderTreeView({
  node,
  selected,
  onSelect,
  depth = 0,
}: {
  node: FolderNode;
  selected: string | null;
  onSelect: (p: string | null) => void;
  depth?: number;
}) {
  const nodeSel = node.path === '' ? null : node.path;
  const isSelected = selected === nodeSel;
  const textProps = isSelected ? { c: 'blue' as const, fw: 600 } : { fw: 400 };
  return (
    <Stack gap={2}>
      <Text
        size="sm"
        pl={depth * 12}
        {...textProps}
        style={{ cursor: 'pointer' }}
        onClick={() => onSelect(nodeSel)}
      >
        {node.name || '(all)'}
      </Text>
      {node.children.map((c) => (
        <FolderTreeView key={c.path} node={c} selected={selected} onSelect={onSelect} depth={depth + 1} />
      ))}
    </Stack>
  );
}

function OcrJobsModal({
  file,
  onClose,
}: {
  file: PipelineIngestFile | null;
  onClose: () => void;
}) {
  const { data: providers = [] } = useOcrProviders();
  const { data: jobs = [] } = useOcrJobsForFile(file?.id ?? null);
  const submit = useSubmitOcrJob();
  const [providerSlug, setProviderSlug] = useState<string | null>(null);

  const enabledProviders = providers.filter((p) => p.enabled);

  return (
    <Modal opened={!!file} onClose={onClose} title="Run OCR" size="lg">
      {file && (
        <Stack gap="md">
          <Text size="sm">
            File: <b>{file.display_name ?? file.original_filename}</b>
          </Text>

          <Group align="flex-end">
            <Select
              label="Provider"
              data={enabledProviders.map((p) => ({ value: p.slug, label: p.display_name }))}
              value={providerSlug}
              onChange={setProviderSlug}
              placeholder={
                enabledProviders.length === 0
                  ? 'No providers enabled — superadmin must toggle one on first'
                  : 'Select a provider'
              }
              disabled={enabledProviders.length === 0}
              w={300}
            />
            <Button
              disabled={!providerSlug || submit.isPending}
              onClick={async () => {
                if (!providerSlug) return;
                try {
                  await submit.mutateAsync({ fileId: file.id, provider_slug: providerSlug });
                  notifications.show({ title: 'Submitted', message: 'Job recorded', color: 'pine' });
                } catch (err) {
                  notifications.show({
                    title: 'Submit failed',
                    message: err instanceof Error ? err.message : 'unknown',
                    color: 'red',
                  });
                }
              }}
            >
              Run
            </Button>
          </Group>

          <Stack gap={4}>
            <Title order={5}>Job history</Title>
            {jobs.length === 0 ? (
              <Text size="sm" c="dimmed">
                No jobs yet.
              </Text>
            ) : (
              jobs.map((j) => (
                <Card key={j.id} withBorder padding="xs">
                  <Group justify="space-between">
                    <Stack gap={0}>
                      <Text size="sm" fw={500}>
                        {j.provider_display_name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {dayjs(j.submitted_at).format('YYYY-MM-DD HH:mm')}
                        {j.error_code && ` — ${j.error_code}`}
                      </Text>
                    </Stack>
                    <Badge
                      color={
                        j.status === 'success'
                          ? 'pine'
                          : j.status === 'failed' || j.status === 'cancelled'
                          ? 'red'
                          : 'blue'
                      }
                      variant="light"
                    >
                      {j.status}
                    </Badge>
                  </Group>
                </Card>
              ))
            )}
          </Stack>
        </Stack>
      )}
    </Modal>
  );
}
